import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { dischargePatient } from '../../../services/admin/patient.service';
import {
  DischargeReadiness,
  PatientLifecycleService,
  toIsoDate,
} from '../../../services/patientmr/patientLifecycle.service';
import { AGED_OUT_AGE, DISCHARGE_REASON_OPTIONS } from '../../../constants/patientLifecycle';
import { ageInYears } from '../../../utils/patientAgeBanners';
import type { DischargeReasonCode, PatientData } from '../../../dtos/admin/patient.dto';
import { ScheduleService, scheduleErrorMessage } from '../../../services/scheduling';
import type { DischargeNotice } from '../../../dtos/patientmr/dischargeSummary.dto';
import { missingNoticeFields } from '../../../utils/dischargeNotice';
import { describeApiError } from '../../../utils/apiErrors';
import DischargeNoticeFields from './DischargeNoticeFields';

export interface DischargeDialogProps {
  open: boolean;
  patientId: string;
  /** Patient DOB (YYYY-MM-DD) — AGED_OUT is pre-selected at 21 or older (DSC-09) */
  dob?: string | null;
  onCancel: () => void;
  /** Called with the refreshed (inactive) patient after the discharge is saved */
  onDischarged: (patient: PatientData) => void;
}

type ReadinessState = (DischargeReadiness & { errors: string[] }) | null;

/**
 * Discharge a patient (DSC-02/05). Shows a readiness summary first (DSC-03): open PAs, unsigned
 * POC, future shifts with a one-click bulk cancel (reason Discharged, Phase 9A; it also ends the
 * patient's recurring series), visits with pending EVV (Phase 10) and unbilled visits (Phase 11).
 * Unbilled visits never block discharge; they stay billable afterwards (through the date of death).
 */
const DischargeDialog: React.FC<DischargeDialogProps> = ({ open, patientId, dob, onCancel, onDischarged }) => {
  const baseId = useId();
  const [reason, setReason] = useState<DischargeReasonCode | ''>('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [deceasedDate, setDeceasedDate] = useState('');
  const [notice, setNotice] = useState<Partial<DischargeNotice>>({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<ReadinessState>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelMessage, setCancelMessage] = useState<string | null>(null);

  const cancelFutureShifts = async () => {
    setCancelBusy(true);
    setCancelMessage(null);
    try {
      const r = await ScheduleService.bulk('cancelPatientFuture', { patientId, code: 'DISCHARGED' });
      setReadiness((prev) => (prev ? { ...prev, futureShiftCount: 0 } : prev));
      setCancelMessage(`Cancelled ${r.cancelled ?? 0} shift(s).`);
    } catch (err) {
      setCancelMessage(scheduleErrorMessage(err, 'Could not cancel the shifts.'));
    } finally {
      setCancelBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const age = ageInYears(dob ?? null);
    setReason(age !== null && age >= AGED_OUT_AGE ? 'AGED_OUT' : '');
    setDischargeDate(toIsoDate(new Date()));
    setDeceasedDate('');
    setNotice({});
    setSubmitted(false);
    setError(null);
    setReadiness(null);
    setCancelMessage(null);
    setReadinessLoading(true);
    let cancelled = false;
    PatientLifecycleService.getDischargeReadiness(patientId)
      .then((result) => {
        if (!cancelled) setReadiness(result);
      })
      .catch(() => {
        if (!cancelled) setReadiness({ openPriorAuths: [], unsignedPocs: [], futureShiftCount: 0, pendingEvvVisits: [], unbilledVisits: { count: 0, oldestDateOfService: null }, errors: ['readiness'] });
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, patientId, dob]);

  const pendingEvv = readiness?.pendingEvvVisits ?? [];
  const unbilled = readiness?.unbilledVisits ?? { count: 0, oldestDateOfService: null };
  const isDeceased = reason === 'DECEASED';
  const reasonMissing = !reason;
  const dateMissing = !dischargeDate;
  const deceasedMissing = isDeceased && !deceasedDate;
  // Phase 16E (DSC-19): §558.295 notice record, not for a Deceased discharge
  const noticeMissing = isDeceased ? [] : missingNoticeFields(notice, dischargeDate);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (reasonMissing || dateMissing || deceasedMissing || noticeMissing.length > 0 || busy || !reason) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await dischargePatient(patientId, {
        dischargeReason: reason,
        dischargeDate,
        deceasedDate: isDeceased ? deceasedDate : undefined,
        dischargeNotice: isDeceased ? undefined : (notice as DischargeNotice),
      });
      onDischarged(updated);
    } catch (err) {
      setError(describeApiError(err, err instanceof Error ? err.message : 'Unable to discharge the patient.'));
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${baseId}-title`;
  const reasonId = `${baseId}-reason`;
  const dateId = `${baseId}-date`;
  const deceasedId = `${baseId}-deceased`;

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={titleId} size="xl">
      <form onSubmit={handleSubmit} className="text-left" noValidate>
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          Discharge patient
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          The chart becomes read-only and the patient leaves the census.
        </p>

        <section className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" aria-label="Discharge readiness">
          <p className="font-medium text-slate-800">Before you discharge</p>
          {readinessLoading || !readiness ? (
            <p className="mt-1 text-slate-600">Checking open items…</p>
          ) : (
            <ul className="mt-1 space-y-1 text-slate-700" data-testid="discharge-readiness">
              <li>
                Open prior authorizations: <strong>{readiness.openPriorAuths.length}</strong>
                {readiness.openPriorAuths.length > 0 && (
                  <span className="text-slate-500">
                    {' '}
                    ({readiness.openPriorAuths.map((pa) => `${pa.label} · ${pa.status}`).join(', ')})
                  </span>
                )}
              </li>
              <li>
                Unsigned POC certifications: <strong>{readiness.unsignedPocs.length}</strong>
              </li>
              <li>
                Future scheduled shifts: <strong>{readiness.futureShiftCount}</strong>{' '}
                <span className="text-slate-500">(not cancelled automatically)</span>
                {readiness.futureShiftCount > 0 && (
                  <button
                    type="button"
                    onClick={cancelFutureShifts}
                    disabled={cancelBusy || busy}
                    className="ml-2 min-h-[36px] rounded-md border border-red-300 bg-white px-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {cancelBusy ? 'Cancelling…' : `Cancel all ${readiness.futureShiftCount} (Discharged)`}
                  </button>
                )}
                {cancelMessage && <span className="ml-2 text-xs text-slate-600" role="status">{cancelMessage}</span>}
              </li>
              <li data-testid="discharge-pending-evv">
                Visits with pending EVV: <strong>{pendingEvv.length}</strong>
                {pendingEvv.length > 0 && (
                  <span className="text-slate-500">
                    {' '}
                    ({pendingEvv.slice(0, 5).map((v) => v.visitDate).join(', ')}
                    {pendingEvv.length > 5 ? ', …' : ''}; reconcile them in EVV Reconciliation before billing)
                  </span>
                )}
              </li>
              <li data-testid="discharge-unbilled">
                Unbilled visits: <strong>{unbilled.count}</strong>
                {unbilled.count > 0 && (
                  <span className="text-slate-500">
                    {' '}
                    (oldest {unbilled.oldestDateOfService}; they can still be billed after discharge)
                  </span>
                )}
              </li>
              {readiness.errors.length > 0 && (
                <li className="text-amber-800">Couldn't load: {readiness.errors.join(', ')}.</li>
              )}
            </ul>
          )}
        </section>

        <label htmlFor={reasonId} className="block mt-4 text-sm font-medium text-slate-700">
          Discharge reason <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <select
          id={reasonId}
          value={reason}
          onChange={(e) => setReason(e.target.value as DischargeReasonCode | '')}
          disabled={busy}
          aria-invalid={submitted && reasonMissing}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm"
        >
          <option value="">Select a reason…</option>
          {DISCHARGE_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {submitted && reasonMissing && <p className="mt-1 text-xs text-red-700">Select a discharge reason.</p>}

        <label htmlFor={dateId} className="block mt-3 text-sm font-medium text-slate-700">
          Discharge date <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <input
          id={dateId}
          type="date"
          value={dischargeDate}
          onChange={(e) => setDischargeDate(e.target.value)}
          disabled={busy}
          aria-invalid={submitted && dateMissing}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
        />
        {submitted && dateMissing && <p className="mt-1 text-xs text-red-700">Enter the discharge date.</p>}

        {isDeceased && (
          <>
            <label htmlFor={deceasedId} className="block mt-3 text-sm font-medium text-slate-700">
              Date of death <span className="text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id={deceasedId}
              type="date"
              value={deceasedDate}
              max={dischargeDate || undefined}
              onChange={(e) => setDeceasedDate(e.target.value)}
              disabled={busy}
              aria-invalid={submitted && deceasedMissing}
              className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
            />
            {submitted && deceasedMissing && (
              <p className="mt-1 text-xs text-red-700">The date of death is required for a Deceased discharge.</p>
            )}
          </>
        )}

        {!isDeceased && (
          <DischargeNoticeFields
            value={notice}
            onChange={setNotice}
            dischargeDate={dischargeDate}
            missing={submitted ? noticeMissing : []}
            disabled={busy}
          />
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {busy ? 'Discharging…' : 'Discharge'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default DischargeDialog;
